import React, { useEffect, useMemo, useState } from 'react';
import { Tag } from '@duxton-web/ui';
import debounce from 'lodash/debounce';

import CommonSelect, { CommonSelectProps } from './CommonSelect';
import { useGetServices } from '@/hooks/usePlannerQueries/service-onboarding';
import { ServiceItem } from '@/lib/PlannerMetadata';

const SEARCH_MIN_LENGTH = 2;
const DEBOUNCE_DELAY_MS = 300;

const ServiceSelect = ({
  multiple,
  placeholder = `Please select backend${multiple ? 's' : ''}`,
  ...props
}: Omit<CommonSelectProps, 'values' | 'idKey' | 'onSearch'>) => {
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => setDebouncedSearch(value), DEBOUNCE_DELAY_MS),
    [],
  );

  useEffect(() => {
    return () => {
      debouncedSetSearch.cancel();
    };
  }, [debouncedSetSearch]);

  const handleSearch = (value: string) => {
    if (value.length === 0 || value.length >= SEARCH_MIN_LENGTH) {
      debouncedSetSearch(value);
    } else {
      debouncedSetSearch.cancel();
      setDebouncedSearch('');
    }
  };

  const activeSearch =
    debouncedSearch.length >= SEARCH_MIN_LENGTH ? debouncedSearch : undefined;

  const { data: servicesData } = useGetServices(activeSearch);
  const services = Array.isArray(servicesData) ? servicesData : [];

  const renderServiceLabel = (item: ServiceItem) => {
    const isHelix = item.is_helix;

    return (
      <div className="common-select__tag-row">
        {item.name}
        <span data-filter-ignore>
          <Tag color={isHelix ? 'success' : 'default'} size="xsmall">
            {isHelix ? 'Helix' : 'GrabX'}
          </Tag>
        </span>
      </div>
    );
  };

  return (
    <CommonSelect
      {...{ placeholder, multiple, ...props }}
      values={services}
      idKey="name"
      onSearch={handleSearch}
      valueFn={renderServiceLabel}
    />
  );
};

export default ServiceSelect;
